import {
  cloneElement,
  forwardRef,
  type AriaAttributes,
  type ReactElement,
  type ReactNode,
  type InputHTMLAttributes,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
  type ButtonHTMLAttributes,
} from "react";
import { Search } from "lucide-react";
import {
  InputControl,
  TextareaControl,
} from "../../unified/vendor/core/primitives/FormControl";

interface FieldControlProps extends AriaAttributes {
  id?: string;
}

/** Scientific/theme adapter over the shared native control and field geometry. */
export function Field({
  label,
  htmlFor,
  hint,
  error,
  children,
}: {
  label: string;
  htmlFor: string;
  hint?: string;
  error?: string;
  children: ReactElement<FieldControlProps>;
}) {
  const description = error || hint;
  return (
    <div className="ui-field" data-ui-field>
      <label data-ui-field-label htmlFor={htmlFor}>
        {label}
      </label>
      {cloneElement(children, {
        id: htmlFor,
        "aria-describedby":
          [
            children.props["aria-describedby"],
            description ? `${htmlFor}-description` : undefined,
          ]
            .filter(Boolean)
            .join(" ") || undefined,
        "aria-invalid": error ? true : children.props["aria-invalid"],
      })}
      {description && (
        <p
          data-ui-field-message
          id={`${htmlFor}-description`}
          className={error ? "ui-field-error" : "ui-field-hint"}
          role={error ? "alert" : undefined}
        >
          {description}
        </p>
      )}
    </div>
  );
}
export const Input = forwardRef<
  HTMLInputElement,
  InputHTMLAttributes<HTMLInputElement>
>(({ className = "", type = "text", ...props }, ref) =>
  ["checkbox", "radio", "range", "color", "hidden"].includes(type) ? (
    <input
      {...props}
      ref={ref}
      type={type}
      className={className}
      data-ui-form-control="none"
    />
  ) : (
    <InputControl
      {...props}
      ref={ref}
      type={type}
      controlSize="field"
      className={`ui-input ${className}`}
    />
  ),
);
export const Select = forwardRef<
  HTMLSelectElement,
  SelectHTMLAttributes<HTMLSelectElement>
>(({ className = "", ...props }, ref) => (
  <select
    {...props}
    ref={ref}
    data-ui-form-control={
      props.multiple || (props.size ?? 0) > 1 ? "none" : "select"
    }
    data-ui-form-trailing-icon={
      props.multiple || (props.size ?? 0) > 1 ? undefined : true
    }
    className={`ui-select ${className}`}
  />
));
export const Textarea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement>
>(({ className = "", ...props }, ref) => (
  <TextareaControl
    {...props}
    ref={ref}
    className={`ui-textarea ${className}`}
  />
));
export const SearchField = forwardRef<
  HTMLInputElement,
  Omit<InputHTMLAttributes<HTMLInputElement>, "type"> & { label: string }
>(({ label, className = "", ...props }, ref) => (
  <label className={`ui-search ${className}`} data-ui-search-shell data-ui-focus-owner>
    <Search size={18} aria-hidden="true" />
    <input
      {...props}
      ref={ref}
      type="search"
      aria-label={label}
      data-ui-search-control="flow"
      data-ui-form-control="none"
    />
  </label>
));
export function Button({
  type = "button",
  variant = "primary",
  loading,
  children,
  className = "",
  disabled,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary" | "quiet";
  loading?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      {...props}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={`ui-button ui-button-${variant} ${className}`}
    >
      {children}
    </button>
  );
}
